import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FileArchive, Loader2, Download, Share2, Link2, Ban, ShieldCheck } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PortalEvidence({ vesselId }) {
  const [exports, setExports] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newLink, setNewLink] = useState(null); // {export_id, url, expires_at}
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(() => {
    api
      .get(`/portal/vessels/${vesselId}/evidence-exports`)
      .then(({ data }) => setExports(data.exports || []))
      .catch(() => setExports([]));
  }, [vesselId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/portal/vessels/${vesselId}/evidence-exports`, {});
      toast.success("Insurance evidence package generated.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate package.");
    } finally { setBusy(false); }
  };

  const share = async (id) => {
    try {
      const { data } = await api.post(`/portal/evidence-exports/${id}/share`, { expires_in_days: 14 });
      const url = `${window.location.origin}/evidence/${data.token}`;
      setNewLink({ export_id: id, url, expires_at: data.expires_at });
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied to clipboard."); }
      catch (_) { toast.success("Share link created."); }
      load();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create share link.");
    }
  };

  const revoke = async (id, shareId) => {
    try {
      await api.post(`/portal/evidence-exports/${id}/shares/${shareId}/revoke`);
      toast.success("Share link revoked.");
      if (newLink?.export_id === id) setNewLink(null);
      load();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error("Could not revoke share link.");
    }
  };

  if (exports === null)
    return (
      <div className="bg-white rounded-xl p-4 border border-slate-100 flex justify-center" data-testid="portal-evidence-loading">
        <Loader2 className="h-5 w-5 animate-spin text-[#D4AF37]" />
      </div>
    );

  return (
    <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid="portal-evidence">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium text-[#051329] flex items-center gap-1">
          <FileArchive className="h-4 w-4 text-[#D4AF37]" /> Insurance evidence
        </div>
        <button
          data-testid="portal-evidence-generate"
          onClick={generate}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#051329] disabled:opacity-50 text-white px-3 py-1.5 text-xs font-semibold"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileArchive className="h-3.5 w-3.5" />} Generate package
        </button>
      </div>
      <p className="text-[11px] text-slate-400 mb-3">
        A factual, privacy-safe record of what HMC observed and documented. You control what is shared — HMC makes no
        risk, premium or condition conclusions.
      </p>

      {exports.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="portal-evidence-empty">No evidence packages yet.</p>
      ) : (
        <ul className="space-y-3">
          {exports.map((ex) => (
            <li key={ex.id} className="border border-slate-150 rounded-lg p-3" data-testid={`portal-evidence-${ex.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm text-[#051329]">Package · {ex.generated_at?.slice(0, 10)}</div>
                  <div className="text-[10px] text-slate-400 font-mono truncate" title={ex.content_hash}>
                    SHA-256 {ex.content_hash?.slice(0, 16)}… · {ex.photo_count} photo(s)
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    data-testid={`portal-evidence-download-${ex.id}`}
                    href={`${BACKEND}/api/portal/evidence-exports/${ex.id}/download`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-[#051329]"
                  >
                    <Download className="h-3.5 w-3.5" /> PDF
                  </a>
                  <button
                    data-testid={`portal-evidence-share-${ex.id}`}
                    onClick={() => share(ex.id)}
                    className="inline-flex items-center gap-1 rounded-lg bg-[#051329] px-2.5 py-1.5 text-xs text-white"
                  >
                    <Share2 className="h-3.5 w-3.5" /> Share
                  </button>
                </div>
              </div>

              {newLink?.export_id === ex.id && (
                <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 p-2" data-testid={`portal-evidence-newlink-${ex.id}`}>
                  <div className="text-[11px] text-emerald-800 flex items-center gap-1"><Link2 className="h-3.5 w-3.5" /> Read-only link (copy now):</div>
                  <div className="text-[11px] font-mono break-all text-emerald-900">{newLink.url}</div>
                </div>
              )}

              <EvidenceShares exportId={ex.id} refreshKey={refreshKey} onRevoke={revoke} />
            </li>
          ))}
        </ul>
      )}
      <p className="text-[10px] text-slate-400 mt-3 flex items-start gap-1">
        <ShieldCheck className="h-3 w-3 mt-0.5 shrink-0" /> Shared links are read-only, expiring and revocable. No account is created for the recipient.
      </p>
    </div>
  );
}

const EvidenceShares = ({ exportId, refreshKey, onRevoke }) => {
  const [shares, setShares] = useState([]);
  useEffect(() => {
    api.get(`/portal/evidence-exports/${exportId}`).then(({ data }) => setShares(data.shares || [])).catch(() => {});
  }, [exportId, refreshKey]);
  const active = shares.filter((s) => s.active);
  if (active.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1" data-testid={`portal-evidence-shares-${exportId}`}>
      {active.map((s) => (
        <li key={s.id} className="flex items-center justify-between text-[11px] text-slate-500">
          <span>Active link · expires {s.expires_at?.slice(0, 10)}</span>
          <button
            data-testid={`portal-evidence-revoke-${s.id}`}
            onClick={() => onRevoke(exportId, s.id)}
            className="inline-flex items-center gap-1 text-red-600"
          >
            <Ban className="h-3 w-3" /> Revoke
          </button>
        </li>
      ))}
    </ul>
  );
};
